import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ShieldOff, KeyRound, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageBody, PageHeader } from '@/components/AppShell';
import {
  mfaApi,
  authApi,
  ApiError,
  type MfaSetupResult,
  type MfaProofInput,
} from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';

type Step = 'overview' | 'setup' | 'recovery' | 'disable' | 'regenerate';

/**
 * Configurações → Segurança: gestão self-service do MFA (TOTP). Ver ADR-0317.
 * Disponível a qualquer usuário autenticado (sobre a própria conta).
 */
export function SegurancaPage() {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('overview');
  const [setupData, setSetupData] = useState<MfaSetupResult | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const statusQuery = useQuery({ queryKey: ['mfa-status'], queryFn: mfaApi.status });

  /** Sincroniza o flag mfaEnabled no store (Topbar, etc.) após mudanças. */
  async function refreshSession() {
    qc.invalidateQueries({ queryKey: ['mfa-status'] });
    const me = await authApi.me();
    useAuthStore.getState().setUser(me);
  }

  function resetToOverview() {
    setStep('overview');
    setSetupData(null);
    setRecoveryCodes(null);
  }

  return (
    <>
      <PageHeader title="Segurança" breadcrumb="Configurações" />
      <PageBody>
        <div className="max-w-xl">
          {statusQuery.isLoading && (
            <div className="bg-card border rounded-lg p-6 text-muted-foreground">Carregando…</div>
          )}

          {statusQuery.data && step === 'overview' && (
            <Overview
              enabled={statusQuery.data.enabled}
              enrolledAt={statusQuery.data.enrolledAt}
              recoveryCodesRemaining={statusQuery.data.recoveryCodesRemaining}
              onEnable={() => setStep('setup')}
              onDisable={() => setStep('disable')}
              onRegenerate={() => setStep('regenerate')}
            />
          )}

          {step === 'setup' && (
            <SetupFlow
              setupData={setupData}
              setSetupData={setSetupData}
              onEnabled={async (codes) => {
                setRecoveryCodes(codes);
                setStep('recovery');
                await refreshSession();
              }}
              onCancel={resetToOverview}
            />
          )}

          {step === 'recovery' && recoveryCodes && (
            <RecoveryCodes codes={recoveryCodes} onDone={resetToOverview} />
          )}

          {step === 'disable' && (
            <ProofForm
              title="Desativar verificação em duas etapas"
              description="Confirme com um código do autenticador ou sua senha atual. Isso remove o 2º fator da sua conta."
              confirmLabel="Desativar MFA"
              destructive
              action={(proof) => mfaApi.disable(proof)}
              onDone={async () => {
                await refreshSession();
                resetToOverview();
              }}
              onCancel={resetToOverview}
            />
          )}

          {step === 'regenerate' && (
            <ProofForm
              title="Gerar novos códigos de recuperação"
              description="Confirme com um código do autenticador ou sua senha. Os códigos antigos deixam de funcionar."
              confirmLabel="Gerar novos códigos"
              action={(proof) => mfaApi.regenerateRecoveryCodes(proof)}
              onDone={async (res) => {
                if (res && 'recoveryCodes' in res) {
                  setRecoveryCodes(res.recoveryCodes);
                  setStep('recovery');
                  await refreshSession();
                } else {
                  resetToOverview();
                }
              }}
              onCancel={resetToOverview}
            />
          )}
        </div>
      </PageBody>
    </>
  );
}

function Overview({
  enabled,
  enrolledAt,
  recoveryCodesRemaining,
  onEnable,
  onDisable,
  onRegenerate,
}: {
  enabled: boolean;
  enrolledAt: string | null;
  recoveryCodesRemaining: number;
  onEnable: () => void;
  onDisable: () => void;
  onRegenerate: () => void;
}) {
  return (
    <div className="bg-card border rounded-lg p-6 space-y-4">
      <div className="flex items-start gap-3">
        {enabled ? (
          <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" aria-hidden />
        ) : (
          <ShieldOff className="w-6 h-6 text-muted-foreground shrink-0" aria-hidden />
        )}
        <div className="space-y-1">
          <h2 className="font-semibold text-fg">Verificação em duas etapas (MFA)</h2>
          {enabled ? (
            <p className="text-sm text-muted-foreground">
              Ativa desde {enrolledAt ? new Date(enrolledAt).toLocaleDateString('pt-BR') : '—'}.
              Você tem <strong>{recoveryCodesRemaining}</strong> código(s) de recuperação
              restante(s).
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Adicione uma camada extra de segurança exigindo um código do seu celular além da
              senha ao entrar.
            </p>
          )}
        </div>
      </div>

      {recoveryCodesRemaining === 0 && enabled && (
        <div className="text-sm text-amber-700 bg-amber-100 border border-amber-200 rounded-md p-3">
          Você não tem mais códigos de recuperação. Gere novos para não correr risco de
          ficar bloqueado.
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {enabled ? (
          <>
            <Button variant="outline" onClick={onRegenerate}>
              <KeyRound className="w-4 h-4 mr-1.5" /> Novos códigos de recuperação
            </Button>
            <Button variant="destructive" onClick={onDisable}>
              Desativar MFA
            </Button>
          </>
        ) : (
          <Button onClick={onEnable}>
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Ativar MFA
          </Button>
        )}
      </div>
    </div>
  );
}

function SetupFlow({
  setupData,
  setSetupData,
  onEnabled,
  onCancel,
}: {
  setupData: MfaSetupResult | null;
  setSetupData: (d: MfaSetupResult) => void;
  onEnabled: (codes: string[]) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const setupMut = useMutation({
    mutationFn: mfaApi.setup,
    onSuccess: (d) => setSetupData(d),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Erro ao iniciar configuração'),
  });

  const enableMut = useMutation({
    mutationFn: () => mfaApi.enable(code.trim()),
    onSuccess: (res) => onEnabled(res.recoveryCodes),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Código inválido'),
  });

  // Inicia o setup automaticamente ao entrar na etapa, se ainda não há QR.
  if (!setupData && !setupMut.isPending && !setupMut.isError) {
    setupMut.mutate();
  }

  return (
    <div className="bg-card border rounded-lg p-6 space-y-4">
      <h2 className="font-semibold text-fg">Ativar verificação em duas etapas</h2>

      {!setupData ? (
        <p className="text-sm text-muted-foreground">Gerando QR code…</p>
      ) : (
        <>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
            <li>Abra seu app autenticador (Google Authenticator, Authy, 1Password…).</li>
            <li>Escaneie o QR code abaixo (ou digite a chave manualmente).</li>
            <li>Digite o código de 6 dígitos gerado para confirmar.</li>
          </ol>

          <div className="flex flex-col items-center gap-3 py-2">
            <img
              src={setupData.qrcodeDataUrl}
              alt="QR code para configurar o MFA"
              className="w-44 h-44 rounded-md border bg-white p-2"
            />
            <code className="text-xs bg-muted px-2 py-1 rounded select-all break-all">
              {setupData.secret}
            </code>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-code">Código de confirmação</Label>
            <Input
              id="confirm-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
        </>
      )}

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" onClick={onCancel} disabled={enableMut.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={() => {
            setError(null);
            enableMut.mutate();
          }}
          disabled={!setupData || code.trim().length < 6 || enableMut.isPending}
        >
          {enableMut.isPending ? 'Ativando…' : 'Confirmar e ativar'}
        </Button>
      </div>
    </div>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard?.writeText(codes.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="bg-card border rounded-lg p-6 space-y-4">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" aria-hidden />
        <div className="space-y-1">
          <h2 className="font-semibold text-fg">Guarde seus códigos de recuperação</h2>
          <p className="text-sm text-muted-foreground">
            Cada código funciona <strong>uma única vez</strong> caso você perca acesso ao
            autenticador. Guarde-os em local seguro — eles não serão exibidos novamente.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 bg-muted/40 border rounded-md p-4 font-mono text-sm">
        {codes.map((c) => (
          <span key={c} className="select-all">
            {c}
          </span>
        ))}
      </div>

      <div className="flex justify-between items-center pt-1">
        <Button variant="outline" onClick={copy}>
          {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
          {copied ? 'Copiado' : 'Copiar códigos'}
        </Button>
        <Button onClick={onDone}>Concluído</Button>
      </div>
    </div>
  );
}

function ProofForm<T>({
  title,
  description,
  confirmLabel,
  destructive,
  action,
  onDone,
  onCancel,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  action: (proof: MfaProofInput) => Promise<T>;
  onDone: (result: T) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      action({ code: code.trim() || undefined, password: password || undefined }),
    onSuccess: (res) => onDone(res),
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : 'Falha ao confirmar. Verifique os dados.'),
  });

  return (
    <div className="bg-card border rounded-lg p-6 space-y-4">
      <div className="space-y-1">
        <h2 className="font-semibold text-fg">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="proof-code">Código do autenticador</Label>
        <Input
          id="proof-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </div>
      <div className="text-center text-xs text-muted-foreground">— ou —</div>
      <div className="space-y-2">
        <Label htmlFor="proof-password">Senha atual</Label>
        <Input
          id="proof-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" onClick={onCancel} disabled={mut.isPending}>
          Cancelar
        </Button>
        <Button
          variant={destructive ? 'destructive' : 'default'}
          onClick={() => {
            setError(null);
            mut.mutate();
          }}
          disabled={mut.isPending || (!code.trim() && !password)}
        >
          {mut.isPending ? 'Confirmando…' : confirmLabel}
        </Button>
      </div>
    </div>
  );
}
